import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Lock, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createCheckoutSession } from "../lib/api";

export default function CheckoutPage() {
  const { items, subtotal, shipping, total } = useCart();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h1 className="font-display text-4xl mb-4">Your cart is empty</h1>
        <Link to="/shop" className="ssm-underline-link text-sm uppercase tracking-[0.2em]">
          Return to shop
        </Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast("Please enter your email");
      return;
    }
    setSubmitting(true);
    try {
      const { url } = await createCheckoutSession({ items, email });
      if (!url) {
        throw new Error("Stripe did not return a checkout URL");
      }
      window.location.href = url;
    } catch (err) {
      toast.error(err.message || "Unable to start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      <Link to="/" className="font-display text-2xl font-medium inline-block mb-8">
        Simple Style Mode
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <form onSubmit={onSubmit} className="lg:col-span-7 space-y-10" data-testid="checkout-form">
          <section>
            <h2 className="font-display text-2xl font-medium mb-2">Contact</h2>
            <p className="text-sm text-neutral-500 mb-5">
              We'll send your order confirmation here.
            </p>
            <label className="block">
              <div className="label-xs mb-2">Email</div>
              <input
                data-testid="checkout-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[color:var(--ssm-border)] bg-white py-3 px-3 text-sm focus:outline-none focus:border-black"
                placeholder="you@example.com"
              />
            </label>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-2xl font-medium">Payment &amp; Shipping</h2>
              <div className="inline-flex items-center gap-1 text-xs text-neutral-500">
                <Lock size={12} /> Secure · Powered by Stripe
              </div>
            </div>
            <div className="border border-[color:var(--ssm-border)] p-5 bg-[color:var(--ssm-surface)] text-sm text-neutral-700 leading-relaxed">
              <p>
                You'll be redirected to Stripe's secure checkout to enter your
                card details and shipping address. No payment information ever
                touches our servers.
              </p>
              <div className="mt-4 pt-3 border-t border-[color:var(--ssm-border)] flex items-center gap-1.5 text-xs text-neutral-500">
                <Lock size={11} /> Secure checkout powered by Stripe
              </div>
            </div>
          </section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-place-order-btn"
            className="w-full py-4 bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-neutral-800 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting
              ? "Redirecting to Stripe…"
              : `Pay · $${total.toFixed(2)}`}
            {!submitting && <ArrowRight size={14} />}
          </button>

          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Shield size={12} /> Your payment is encrypted and handled by Stripe.
          </div>
        </form>

        <aside className="lg:col-span-5">
          <div className="bg-[color:var(--ssm-surface)] p-8 sticky top-28">
            <h2 className="font-display text-2xl font-medium mb-6">Order Summary</h2>
            <div className="space-y-4 mb-6 max-h-80 overflow-y-auto pr-2">
              {items.map((item) => (
                <div
                  key={`${item.product_id}-${item.size}-${item.color || "default"}`}
                  className="flex gap-4 items-start"
                >
                  <div className="relative shrink-0">
                    <img src={item.image} alt="" className="w-16 h-20 object-cover bg-neutral-100" />
                    <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
                      {item.quantity}
                    </span>
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium leading-tight">{item.name}</div>
                    <div className="text-neutral-500 text-xs mt-1">
                      {item.color ? `${item.color} · ` : ""}Size {item.size}
                    </div>
                  </div>
                  <div className="text-sm">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-[color:var(--ssm-border)] pt-4 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600">Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600">Shipping</span>
                <span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between text-base font-medium pt-2 border-t border-[color:var(--ssm-border)]">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
